import { useMemo } from 'react';

import useTicketListQuery from 'src/components/followUp/hooks/useTicketListQuery';
import { useTicketTableContext } from 'src/components/followUp/TicketFilterContext';

export const TICKETS_PER_PAGE = 10;

export interface TicketPagination {
  previousTicket?: { id: number; page: number };
  nextTicket?: { id: number; page: number };
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  previousPageIndex: number;
  nextPageIndex: number;
  firstTicketNumberOnPage: number;
  lastTicketNumberOnPage: number;
}

const useTicketTablePagination = (): TicketPagination => {
  const {
    tickets,
    page,
    selectedTicket,
    totalTickets,
    debouncedSearchString,
    sortBy,
  } = useTicketTableContext();
  // hasPreviousPage and hasNextPage are only used for the table pagination, which does not loop
  const hasPreviousPage = useMemo(() => page > 1, [page]);
  const hasNextPage = useMemo(() => {
    if (!totalTickets) return false;
    return page * TICKETS_PER_PAGE < totalTickets;
  }, [page, totalTickets]);

  const totalPages = useMemo(
    () => (totalTickets ? Math.ceil(totalTickets / TICKETS_PER_PAGE) : 1),
    [totalTickets],
  );

  const previousPageIndex = useMemo(() => {
    // If the page is not the first page, return the previous page
    if (page > 1) return page - 1;

    // else return the last page
    return totalPages;
  }, [page, totalPages]);

  const nextPageIndex = useMemo(() => {
    // If the page is not the last page, return the next page
    if (page < totalPages) return page + 1;

    // else return the first page
    return 1;
  }, [page, totalPages]);

  const { data: previousPageTickets } = useTicketListQuery(
    previousPageIndex,
    debouncedSearchString,
    sortBy,
    previousPageIndex !== page, // Only fetch the previous page if it's not the current page
  );

  const { data: nextPageTickets } = useTicketListQuery(
    nextPageIndex,
    debouncedSearchString,
    sortBy,
    nextPageIndex !== page, // Only fetch the next page if it's not the current page
  );

  const selectedTicketIndex = useMemo(
    () => tickets?.findIndex((ticket) => ticket.id === selectedTicket?.id),
    [tickets, selectedTicket],
  );

  const previousTicket = useMemo(() => {
    if (
      !tickets ||
      selectedTicketIndex === undefined ||
      selectedTicketIndex === -1
    )
      return undefined;

    // If the selected ticket is not the first ticket on the page, return the previous ticket
    if (selectedTicketIndex > 0) {
      return { id: tickets[selectedTicketIndex - 1].id, page };
    }

    // If the previous page index is the same as the current page index, return the last ticket
    if (previousPageIndex === page) {
      return { id: tickets[tickets.length - 1].id, page };
    }

    // Wait for the previous page tickets to be loaded
    if (!previousPageTickets) return undefined;

    // Return the last ticket of the previous page
    return {
      id: previousPageTickets.results[previousPageTickets.results.length - 1]
        .id,
      page: previousPageIndex,
    };
  }, [previousPageIndex, previousPageTickets, selectedTicketIndex, tickets]);

  const nextTicket = useMemo(() => {
    if (
      !tickets ||
      selectedTicketIndex === undefined ||
      selectedTicketIndex === -1
    )
      return undefined;

    // If the selected ticket is not the last ticket on the page, return the next ticket
    if (selectedTicketIndex < tickets.length - 1) {
      return { id: tickets[selectedTicketIndex + 1].id, page };
    }

    // If the next page index is the same as the current page index, return the first ticket
    if (nextPageIndex === page) {
      return { id: tickets[0].id, page };
    }

    // Wait for the next page tickets to be loaded
    if (!nextPageTickets) return undefined;

    // Return the first ticket of the next page
    return { id: nextPageTickets.results[0].id, page: nextPageIndex };
  }, [nextPageIndex, nextPageTickets, selectedTicketIndex, tickets]);

  const firstTicketNumberOnPage = useMemo(
    () => (page - 1) * TICKETS_PER_PAGE + 1,
    [page],
  );
  const lastTicketNumberOnPage = useMemo(
    () => Math.min(page * TICKETS_PER_PAGE, totalTickets ?? 0),
    [page, totalTickets],
  );

  return {
    hasPreviousPage,
    hasNextPage,
    previousPageIndex,
    nextPageIndex,
    firstTicketNumberOnPage,
    lastTicketNumberOnPage,
    previousTicket,
    nextTicket,
  };
};

export default useTicketTablePagination;
